import * as React from 'react'
import { Accordion, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IFText from 'Components/IFText/IFText'
import Styles from './CardSessionListItem.module.css'
import Colors from 'Theme/Colors'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import Moment from 'moment'
import icons from 'Theme/Icons'
import PropTypes from 'prop-types'
import InfinityEnums from 'Enums/InfinityEnums'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { isPropsMatch } from 'Utils/PropsMatch'
import moment from 'moment'
import ReactTimeAgo from 'react-time-ago'
import { styled as muiStyled } from '@mui/material/styles'

const StyledHyperLink = styled.a`
  font-size: 14;
  vertical-align: middle;
  text-align: center;
  padding-bottom: 2px;
  margin-left: 6px;
  color: ${(props) => props.textColor};
  &:hover {
    color: ${(props) => props.hoverColor} !important;
  }
  &:focus {
    color: ${(props) => props.hoverColor} !important;
  }
`
const StatusContainer = styled.div`
  background-color: ${Colors.GreyBackground};
  color: ${(props) => props.status};
`

function CardSessionListItem({
  connectorUid,
  connectorType,
  energyConsumption,
  startedAt,
  stationName,
  chargePointName,
  reservationID = '-',
  chargingLevel,
  chargePointId,
  stationId,
  connectorStatus,
  isChargePointOffline,
  ...props
}) {
  const { t } = useTranslation()
  const durationHr = parseInt(Moment(Date.now()).diff(startedAt, 'hours'))
  const durationMin = moment
    .duration(moment(Date.now()).diff(moment(startedAt)))
    .minutes()
  const durationSecs = moment
    .duration(moment(Date.now()).diff(moment(startedAt)))
    .seconds()

  const AccordionSummary = muiStyled(MuiAccordionSummary)(({ theme }) => ({
    boxShadow: '0px 0px 0px 0px',
    borderRadius: '5px',
    padding: '0px',
    '&.Mui-expanded': {
      minHeight: 64,
      '& .MuiAccordionSummary-content': {
        margin: '0px',
      },
    },
    '&:hover': {
      backgroundColor: Colors.ChargePointLogListItemHover,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginRight: '24px',
    },
  }))

  const ConnectorIconSrc = () => {
    switch (connectorType) {
      case InfinityEnums.ConnectorType.IEC_62196_T2:
        return icons.type2({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.CHADEMO:
        return icons.chademo({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.IEC_62196_T2_COMBO:
        return icons.comboCCS({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.DOMESTIC_F:
        return icons.schuko({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.GBT_DC:
        return icons.GBT_DC({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.IEC_62196_T1:
        return icons.type1({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.IEC_62196_T1_COMBO:
        return icons.type1CCS({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.GBT_AC:
        return icons.GBT_AC({
          width: 22,
          height: 22,
          color: Colors.black,
        })
      default:
        return icons.type2({
          width: 22,
          height: 22,
          color: Colors.black,
        })
    }
  }
  const handleStatus = (status) => {
    if (isChargePointOffline) return Colors.OfflineConnector

    switch (status) {
      case InfinityEnums.ConnectorStatus.AVAILABLE:
        return Colors.AvailableConnector
      case InfinityEnums.ConnectorStatus.PREPARING:
        return Colors.PreparingConnector
      case InfinityEnums.ConnectorStatus.CHARGING:
        return Colors.ChargingConnector
      case InfinityEnums.ConnectorStatus.SUSPENDEDEV:
        return Colors.SuspendedConnector
      case InfinityEnums.ConnectorStatus.SUSPENDEDEVSE:
        return Colors.SuspendedConnector
      case InfinityEnums.ConnectorStatus.FINISHING:
        return Colors.FinishingConnector
      // Coarse in-use status post status-model inversion (+ the session-level
      // EVConnected, in case a fine status is ever surfaced here).
      case InfinityEnums.ConnectorStatus.OCCUPIED:
        return Colors.OccupiedConnector
      case InfinityEnums.ChargingSessionChargingState.EVCONNECTED:
        return Colors.SuspendedConnector
      case InfinityEnums.ConnectorStatus.RESERVED:
        return Colors.ReservedConnector
      case InfinityEnums.ConnectorStatus.UNAVAILABLE:
        return Colors.UnAvailableConnector
      case InfinityEnums.ConnectorStatus.OFFLINE:
        return Colors.OfflineConnector
      case InfinityEnums.ConnectorStatus.FAULTED:
        return Colors.FaultedConnector
      default:
        return Colors.black
    }
  }
  return (
    <div {...props}>
      <Accordion
        className={[Styles.accordion]}
        style={{
          boxShadow: `0px 0px 2px ${Colors.ChargePointLogTransactionItemBoxShadow}`,
          borderRadius: '5px',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={Styles.accordionSummary}
        >
          <div className={Styles.container}>
            <div className={Styles.accordionStationWrapper}>
              <img
                className={Styles.ChargingIcon}
                src={icons.fastCharging}
                alt=""
              />
              <div className={Styles.stationText}>
                <StyledHyperLink
                  hoverColor={Colors.primary}
                  textColor={Colors.text}
                  target="_blank"
                  href={`/sites/${stationId}/stations/${chargePointId}`}
                >
                  <div>
                    <IFText className={Styles.StationNameText}>
                      {stationName}
                    </IFText>
                  </div>
                  <IFText className={Styles.chargePointName}>
                    {chargePointName}
                  </IFText>
                </StyledHyperLink>
              </div>
            </div>

            <div className={Styles.accordionTextPower}>
              <div className={Styles.flashIcon}>{icons.flash}</div>
              <IFText className={Styles.accordionTextEnergyConsumption}>
                {`${Number(energyConsumption / 1000).toFixed(3)} kWh`}
              </IFText>
            </div>

            <div className={Styles.accordionDuration}>
              <TimerOutlinedIcon
                style={{ height: '18px' }}
                className={Styles.accordionicon}
              />
              <IFText className={Styles.accordionDurationText}>
                <ReactTimeAgo
                  date={new Date(startedAt)}
                  locale="en-US"
                  timeStyle="mini"
                />
              </IFText>
            </div>
            <div className={Styles.accordionDateContainer}>
              <StatusContainer
                status={handleStatus(connectorStatus)}
                className={Styles.StatusContainer}
              >
                <IFText className={Styles.StatusText}>{connectorStatus}</IFText>
              </StatusContainer>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails className={Styles.accordionDetails}>
          <div className={Styles.container}>
            <div className={Styles.leftDetailsContainer}>
              <div className={Styles.startDateContainer}>
                <IFText className={Styles.startText}>
                  {t('UserTransaction.Start')}
                </IFText>
                <IFText className={Styles.accordionStartDate}>
                  {Moment(startedAt).format('MMM DD, YYYY \u00A0 hh:mm:ss')}
                </IFText>
              </div>
              <div className={Styles.durationContainer}>
                <IFText className={Styles.durationText}>
                  {t('UserTransaction.Duration')}
                </IFText>
                <IFText className={Styles.durationTime}>
                  {(durationHr === 0 || durationHr / 10 < 1 ? '0' : '') +
                    durationHr +
                    ':' +
                    (durationMin === 0 || durationMin / 10 < 1 ? '0' : '') +
                    durationMin +
                    ':' +
                    (durationSecs === 0 || durationSecs / 10 < 1 ? '0' : '') +
                    durationSecs}
                </IFText>
              </div>
              <div className={Styles.connectorTypeContainer}>
                <IFText className={Styles.connectorTypeText}>
                  {t('UserTransaction.ConnectorType')}
                </IFText>
                <div className={Styles.accordionicon}>{ConnectorIconSrc()}</div>
              </div>
            </div>

            <div className={Styles.rightDetailsContainer}>
              <div className={Styles.connectorIDContainer}>
                <IFText className={Styles.connectorIDText}>
                  {t('UserTransaction.ConnectorID')}
                </IFText>
                <IFText className={Styles.connectorID}>{connectorUid}</IFText>
              </div>
              <div className={Styles.chargingLevelContainer}>
                <IFText className={Styles.chargingLevelText}>
                  {t('UserTransaction.ChargingLevel')}
                </IFText>

                <div className={Styles.chargingLevelFlashContainer}>
                  {[
                    ...Array(
                      chargingLevel === InfinityEnums.ChargingSpeedLevels.LEVEL1
                        ? 1
                        : chargingLevel ===
                          InfinityEnums.ChargingSpeedLevels.LEVEL2
                        ? 2
                        : 3,
                    ),
                  ].map(() => {
                    return (
                      <div className={Styles.chargingLevelFlashIcon}>
                        {icons.flash}
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className={Styles.reservationContainer}>
                <IFText className={Styles.reservationText}>
                  {t('UserTransaction.ReservationID')}
                </IFText>
                <IFText className={Styles.reservationID}>
                  {reservationID}
                </IFText>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

CardSessionListItem.propTypes = {
  connectorUid: PropTypes.number.isRequired,
  connectorType: PropTypes.string,
  energyConsumption: PropTypes.number.isRequired,
  startedAt: PropTypes.string.isRequired,
  endedAt: PropTypes.string.isRequired,
  stationName: PropTypes.string.isRequired,
  chargePointName: PropTypes.string.isRequired,
  reservationID: PropTypes.string.isRequired,
  chargingLevel: PropTypes.number.isRequired,
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'connectorUid',
    'connectorType',
    'energyConsumption',
    'startedAt',
    'stationName',
    'chargePointName',
    'reservationID',
    'chargingLevel',
    'chargePointId',
    'stationId',
    'connectorStatus',
    'isChargePointOffline',
  ])
}
export default React.memo(CardSessionListItem, shouldSkipRender)
